import * as Sentry from '@sentry/angular';
import { environment } from 'environments/environment';

/**
 * Sentry bootstrap for the browser app.
 *
 * Why a plain module and not an Angular service: `init()` has to run before
 * `bootstrapApplication`, i.e. before the injector exists. Everything here is
 * therefore free functions, called from `main.ts` and from the two seams that
 * already exist (`PermissionErrorFilteringHandler`, `AppComponent`).
 *
 * Privacy posture (see `src/assets/legal/privacy.en.md` and DESIGN.md):
 *  - `sendDefaultPii: false` — no e-mail, no username, no IP attached.
 *  - `beforeSend` strips the IP defensively and removes query strings and
 *    fragments from every URL it can reach: our routes carry property, contract
 *    and tenant ids, and a query string is where tokens end up.
 *  - Console and DOM breadcrumbs are OFF. DOM breadcrumbs serialise the text of
 *    the clicked element — in Proporio that text is tenant names, addresses and
 *    amounts, i.e. personal data of third parties for which we are a processor
 *    on behalf of our agency customers. Console breadcrumbs are off for the
 *    same reason plus a technical one: `install-console-guard.ts` already
 *    rewrites `console.*` in production, and `permission-error-filter.handler.ts`
 *    patches `console.error` to swallow Firestore permission-denied noise —
 *    capturing the console would either fight those patches or re-import the
 *    very noise they exist to suppress.
 *
 * Legal basis is legitimate interest (GDPR art. 6(1)(f), service security and
 * integrity), which is why this loads without a cookie banner: the SDK writes
 * nothing to the user's device, so it is out of scope of art. 129 of the
 * Belgian law of 13 June 2005. Anything that DOES store on the device (session
 * replay, for one) must stay off unless it is put behind the consent banner.
 */

/** True when this build is configured to talk to Sentry at all. */
export function isSentryEnabled(): boolean {
  return !!environment.features?.enableErrorReporting && (environment.sentry?.dsn ?? '') !== '';
}

/**
 * Remove the query string and fragment from a URL, keeping origin + path.
 * Returns the input untouched when it is not parseable (relative frames,
 * `webpack://` filenames, …) — losing a breadcrumb is preferable to throwing
 * inside `beforeSend`.
 *
 * Exported for the spec: this is the function that decides whether a token in
 * a query string leaves the browser, so it is worth pinning down.
 */
export function stripUrl(url: string): string {
  try {
    const parsed = new URL(url, window.location.origin);
    return parsed.origin + parsed.pathname;
  } catch {
    return url.split(/[?#]/)[0];
  }
}

/**
 * The options `initSentry` passes to the SDK. Exported so the spec can exercise
 * `beforeSend` / `beforeBreadcrumb` — the scrubbing rules are the part of this
 * file a regression would silently turn into a privacy incident.
 */
export function sentryOptions(): Sentry.BrowserOptions {
  return {
    dsn: environment.sentry?.dsn,
    environment: environment.environmentName,
    // Never let the SDK attach identifying data on its own. The only identity
    // we send is the pseudonymous Firebase uid, set explicitly in setSentryUser.
    sendDefaultPii: false,
    tracesSampleRate: environment.sentry?.tracesSampleRate ?? 0,
    // Providing an integration by name REPLACES the default of the same name,
    // so this turns console + DOM breadcrumbs off while keeping the rest of the
    // default set (global handlers, dedupe, http context, …).
    integrations: [Sentry.breadcrumbsIntegration({ console: false, dom: false })],
    ignoreErrors: [
      // Benign browser noise, not actionable and high volume.
      /ResizeObserver loop/,
      /Non-Error promise rejection captured/,
      // Extensions and injected scripts, never our code.
      /^Script error\.?$/
    ],
    beforeBreadcrumb(breadcrumb) {
      const url = breadcrumb.data?.['url'];
      if (typeof url === 'string') {
        breadcrumb.data = { ...breadcrumb.data, url: stripUrl(url) };
      }
      return breadcrumb;
    },
    beforeSend(event) {
      if (event.user) {
        delete event.user.ip_address;
        delete event.user.email;
        delete event.user.username;
      }
      if (event.request?.url) {
        event.request.url = stripUrl(event.request.url);
        delete event.request.query_string;
        delete event.request.cookies;
        delete event.request.headers;
      }
      return event;
    }
  };
}

/**
 * Boot Sentry. Safe to call unconditionally and safe to call twice; no-op when
 * the DSN is empty (local dev, emulator, Karma) so those runs never touch the
 * network. Call once from `main.ts`, before `bootstrapApplication`.
 */
export function initSentry(): void {
  if (!isSentryEnabled()) return;
  Sentry.init(sentryOptions());
}

/**
 * Report an error that survived every filter. No-op when Sentry is disabled,
 * so call sites don't need their own guard.
 *
 * `tags` are indexed and searchable in Sentry, which is what makes an error
 * groupable by cause rather than by stack — a caller that already knows WHICH
 * step broke and WHICH error code came back should say so here. Tags are
 * user-visible in the Sentry UI and are NOT scrubbed by `beforeSend`, so pass
 * only bounded technical values (a step name, an error code); never a name, an
 * address, an amount or an id belonging to a person.
 */
export function captureAppException(
  error: unknown,
  context?: { tags?: Record<string, string> },
): void {
  if (!isSentryEnabled()) return;
  Sentry.captureException(error, context?.tags ? { tags: context.tags } : undefined);
}

/**
 * Attach the pseudonymous identity to subsequent events: the Firebase uid and
 * the current workspace, nothing else. Passing a nullish uid clears it (logout).
 *
 * The uid is a Firebase-generated opaque id, not an identifier the user chose,
 * and it is the same key already used by `product-analytics.service.ts`.
 */
export function setSentryUser(uid: string | null | undefined, workspaceId: string | null | undefined): void {
  if (!isSentryEnabled()) return;
  Sentry.setUser(uid ? { id: uid } : null);
  Sentry.setTag('workspace', workspaceId ?? undefined);
}

/**
 * A `httpsCallable` invocation slower than this is worth a look — used to
 * decide whether issue #484's "cold start after login" premise holds up
 * before building any warm-up mechanism for it.
 */
export const SLOW_CALLABLE_THRESHOLD_MS = 1200;

/**
 * Bucket the time since the last auth resolution into a small, bounded set of
 * labels. A raw millisecond value makes a poor Sentry tag (unbounded
 * cardinality defeats grouping/search) — the buckets are what let us compare
 * "callable fired right after login" against "callable fired mid-session" in
 * the Sentry UI. `null` (no login recorded yet, e.g. a page reload before
 * `handleAuthStateChange` ran) gets its own bucket rather than being dropped.
 */
export function bucketMsSinceLogin(msSinceLogin: number | null): string {
  if (msSinceLogin === null) return 'unknown';
  if (msSinceLogin < 10_000) return '<10s';
  if (msSinceLogin < 30_000) return '10-30s';
  if (msSinceLogin < 60_000) return '30-60s';
  return '>60s';
}

/**
 * Report a `httpsCallable` invocation that took longer than
 * `SLOW_CALLABLE_THRESHOLD_MS`. No-op under the threshold and when Sentry is
 * disabled, so call sites don't need their own guard.
 *
 * Deliberately `captureMessage`, not a performance span: `tracesSampleRate`
 * is 0 in every environment (see `sentryOptions`), so spans would be created
 * and immediately dropped. This is measurement for issue #484 — find out
 * whether callables right after login are actually slower than mid-session
 * ones — not a permanent tracing pipeline.
 *
 * `level: 'info'` and a dedicated fingerprint keep these out of the error
 * stream and any error-rate alerting: they group by callable name, not by
 * message text, and never fire the "new issue" / "error rate spike" rules
 * that watch `level: 'error'`.
 */
export function reportSlowCallable(
  name: string,
  durationMs: number,
  msSinceLogin: number | null,
): void {
  if (!isSentryEnabled()) return;
  if (durationMs <= SLOW_CALLABLE_THRESHOLD_MS) return;
  Sentry.captureMessage('slow_callable', {
    level: 'info',
    fingerprint: ['slow-callable', name],
    tags: { callable: name, msSinceLogin: bucketMsSinceLogin(msSinceLogin) },
    extra: { durationMs, msSinceLogin },
  });
}
